import { useContext, useRef } from 'react';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore jodit-react is in the parent dir
import JoditEditor from 'jodit-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore jodit is in the parent dir
import { IJodit } from 'jodit/esm/types';
import { CheckIcon, CopyIcon } from 'lucide-react';

import { Button } from 'components/Button';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import {
  SidebarStateContext,
  supportsDraftResponse,
} from 'contexts/sidebarStateContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { DraftReplyMode } from 'lib/mailclient/interface';
import { Maybe } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { getJoditEditorConfig } from 'utils/getJoditEditorHelpers';
import { cn } from 'utils/shadcn';

import { useQuickQuoteContext } from '../context/QuickQuoteContext';
import { useDraftRecipients } from '../hooks/useDraftRecipients';
import { CarrierCostType } from '../types';
import { handleCopyToClipboard as handleCopyToClipboardUtil } from '../util';
import { DraftRecipientFields } from './DraftRecipientFields';
import { OutlookQuoteReplyActions } from './OutlookQuoteReplyActions';

interface DraftResponseSectionProps {
  handleDraftResponse: (
    latestDraftBody: string,
    replyMode: DraftReplyMode,
    to: string[],
    cc: string[],
    bcc: string[]
  ) => void;
  handleSendResponse: (
    latestDraftBody: string,
    replyMode: DraftReplyMode,
    to: string[],
    cc: string[],
    bcc: string[]
  ) => void;
  copyToClipboardProperties: Record<string, any>;
  showLinehaulOption?: boolean;
  onCopySuccess?: () => void;
}

export const DraftResponseSection = ({
  handleDraftResponse,
  handleSendResponse,
  copyToClipboardProperties,
  showLinehaulOption = false,
  onCopySuccess,
}: DraftResponseSectionProps) => {
  const joditRef = useRef<Maybe<IJodit>>(null);
  const {
    currentState: { drumkitPlatform, isOutlookReply, isSharedMailboxEmail },
  } = useContext(SidebarStateContext);
  const { isSharedMailboxUser } = useServiceFeatures();
  const isSharedMailbox =
    (isSharedMailboxUser ?? false) || isSharedMailboxEmail;
  const { to, cc, bcc, setTo, setCc, setBcc } = useDraftRecipients();
  const quoteReplyMode = DraftReplyMode.ReplyAll;

  const {
    draftResponse,
    hasCopiedDraftResponse,
    maxDistance,
    finalPriceFormat,
    loadingDraftReply,
    setDraftResponse,
    setUserEditedDraft,
    setFinalPriceFormat,
  } = useQuickQuoteContext();

  const handleCopyToClipboard = async () => {
    await handleCopyToClipboardUtil(draftResponse, onCopySuccess);
  };

  const getLatestDraftResponse = (): string => {
    const editorValue = joditRef.current?.value;

    return typeof editorValue === 'string' ? editorValue : draftResponse;
  };

  const onSendQuoteReply = () => {
    handleSendResponse(getLatestDraftResponse(), quoteReplyMode, to, cc, bcc);
  };

  const onDraftQuoteReply = () => {
    handleDraftResponse(getLatestDraftResponse(), quoteReplyMode, to, cc, bcc);
  };

  if (!supportsDraftResponse(drumkitPlatform)) {
    return null;
  }

  return (
    <div className='w-full min-w-0'>
      <Flex justify='between' align='center' className='mb-0'>
        <Typography
          variant='h5'
          weight='semibold'
          className='text-neutral-800 whitespace-nowrap'
        >
          Draft Response
        </Typography>
        <Button
          buttonNamePosthog={ButtonNamePosthog.CopyQuoteToClipboard}
          logProperties={copyToClipboardProperties}
          className={cn(
            'h-6 w-6 p-0 border-none',
            hasCopiedDraftResponse ? 'cursor-default' : 'cursor-pointer'
          )}
          variant='ghost'
          type='button'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            !hasCopiedDraftResponse && handleCopyToClipboard();
          }}
        >
          {hasCopiedDraftResponse ? (
            <Tooltip open={true}>
              <TooltipTrigger asChild>
                <CheckIcon className='h-4 w-4' />
              </TooltipTrigger>
              <TooltipContent>Copied!</TooltipContent>
            </Tooltip>
          ) : (
            <CopyIcon className='h-4 w-4' />
          )}
        </Button>
      </Flex>

      <DraftRecipientFields
        to={to}
        cc={cc}
        bcc={bcc}
        onToChange={setTo}
        onCcChange={setCc}
        onBccChange={setBcc}
      />

      <div className='mt-3 w-full max-w-full overflow-hidden'>
        {/* Draft Response Textarea */}
        <JoditEditor
          className='mt-2 w-full max-w-full'
          config={getJoditEditorConfig({
            refToUpdateAfterInit: joditRef,
          })}
          value={draftResponse}
          onBlur={(e: any) => {
            setDraftResponse(e);
            setUserEditedDraft(true);
          }}
        />
      </div>

      {/* If distance exists, allow user to toggle format of final price in response */}
      {Boolean(maxDistance) && (
        <Flex
          align='center'
          className='whitespace-nowrap min-w-0 mt-1 overflow-hidden'
        >
          <span className='text-[13px] xxs:text-xs text-neutral-500 mr-2'>
            Show:
          </span>
          <div className='inline-flex rounded-[4px] max-h-5 border border-neutral-400 text-xs'>
            <button
              type='button'
              title='Flat Rate'
              onClick={() => setFinalPriceFormat(CarrierCostType.Flat)}
              className={`px-1.5 transition-colors ${
                finalPriceFormat === CarrierCostType.Flat
                  ? 'text-brand-400 font-medium bg-brand-50'
                  : 'text-neutral-500 hover:text-brand-400'
              }`}
            >
              Flat Rate
            </button>
            <button
              title='Per Mile Rate'
              type='button'
              onClick={() => setFinalPriceFormat(CarrierCostType.PerMile)}
              className={`px-1.5 transition-colors ${
                finalPriceFormat === CarrierCostType.PerMile
                  ? 'text-brand-400 font-medium bg-brand-50'
                  : 'text-neutral-500 hover:text-brand-400'
              }`}
            >
              Per Mile
            </button>
            <button
              title='Both Rates'
              type='button'
              onClick={() => setFinalPriceFormat('Both')}
              className={`px-1.5 transition-colors ${
                finalPriceFormat === 'Both'
                  ? 'text-brand-400 font-medium bg-brand-50'
                  : 'text-neutral-500 hover:text-brand-400'
              }`}
            >
              Both
            </button>
            {showLinehaulOption && (
              <button
                title='Customer Linehaul'
                type='button'
                onClick={() => setFinalPriceFormat(CarrierCostType.Linehaul)}
                className={`px-1.5 transition-colors ${
                  finalPriceFormat === 'Linehaul'
                    ? 'text-brand-400 font-medium bg-brand-50'
                    : 'text-neutral-500 hover:text-brand-400'
                }`}
              >
                Linehaul
              </button>
            )}
          </div>
        </Flex>
      )}

      <OutlookQuoteReplyActions
        drumkitPlatform={drumkitPlatform}
        isSharedMailbox={isSharedMailbox}
        isOutlookReply={isOutlookReply}
        loadingDraftReply={loadingDraftReply}
        disabled={to.length === 0}
        onSend={onSendQuoteReply}
        onDraft={onDraftQuoteReply}
        onCopy={handleCopyToClipboard}
        logProperties={copyToClipboardProperties}
      />
    </div>
  );
};
